import React, { useState } from 'react';
import { API_BASE_URL } from '../../config';
import AdminSidebar from './AdminSidebar';
import './PushNotificationManager.css';

const PushNotificationManager = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeCategory, setActiveCategory] = useState('Announcement');
    const [title, setTitle] = useState('New Chapters Added');
    const [message, setMessage] = useState('Check out the latest study materials added to your course.');
    const [target, setTarget] = useState('all');
    const [specificUserId, setSpecificUserId] = useState('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);

    const categories = [
        { id: 'Offer', name: 'Offers', icon: '🎉', defaultTitle: 'Get 1 Chapter Free Today', defaultMessage: 'Hurry! Limited time offer to unlock extra content for free.' },
        { id: 'Announcement', name: 'Announcements', icon: '📢', defaultTitle: 'New Chapters Added', defaultMessage: 'Check out the latest study materials added to your course.' },
        { id: 'Reminder', name: 'Reminders', icon: '⏰', defaultTitle: 'Complete your test', defaultMessage: 'Your scheduled test is waiting! Don\'t fall behind your goals.' },
        { id: 'Promotion', name: 'Promotions', icon: '💰', defaultTitle: '50% Discount', defaultMessage: 'Upgrade to Premium today at half the price. Valid for 24h.' },
        { id: 'Engagement', name: 'Engagement', icon: '🔥', defaultTitle: 'You are falling behind!', defaultMessage: 'Come back and continue your learning streak. Consistency is key!' }
    ];

    const handleCategorySelect = (category) => {
        setActiveCategory(category.id);
        setTitle(category.defaultTitle);
        setMessage(category.defaultMessage);
    };

    const handleSend = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);

        try {
            const response = await fetch(`${API_BASE_URL}admin_send_push.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    target,
                    user_id: specificUserId,
                    title,
                    message,
                    type: activeCategory
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setStatus({ type: 'success', text: `Sent successfully! ${data.details}` });
            } else {
                setStatus({ type: 'error', text: data.message });
            }
        } catch (error) {
            setStatus({ type: 'error', text: 'Failed to connect to server' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`admin-layout ${!sidebarOpen ? 'sidebar-collapsed' : ''}`}>
            <AdminSidebar sidebarOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
            
            <main className="admin-main">
                <header className="main-header">
                    <button className="sidebar-toggle-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
                    <h1>Push Notification Center</h1>
                    <div className="header-actions">
                        <span className="live-status">● System Online</span>
                    </div>
                </header>

                <div className="notification-workspace">
                    {/* Left: Composer */}
                    <div className="composer-column">
                        <section className="composer-card">
                            <h3>Choose Category</h3>
                            <div className="category-tags">
                                {categories.map(cat => (
                                    <button 
                                        key={cat.id} 
                                        className={`tag-item ${activeCategory === cat.id ? 'active' : ''}`}
                                        onClick={() => handleCategorySelect(cat)}
                                    >
                                        <span className="tag-icon">{cat.icon}</span>
                                        <span className="tag-label">{cat.name}</span>
                                    </button>
                                ))}
                            </div>

                            <form className="composition-form" onSubmit={handleSend}>
                                <div className="input-group">
                                    <label>Recipient Mode</label>
                                    <div className="segment-control">
                                        <button 
                                            type="button"
                                            className={target === 'all' ? 'active' : ''} 
                                            onClick={() => setTarget('all')}
                                        >All Learners</button>
                                        <button 
                                            type="button"
                                            className={target === 'specific' ? 'active' : ''} 
                                            onClick={() => setTarget('specific')}
                                        >Specific ID</button>
                                    </div>
                                </div>

                                {target === 'specific' && (
                                    <div className="input-group animate-slide-down">
                                        <label>Learner ID</label>
                                        <input 
                                            type="number" 
                                            placeholder="Enter numeric ID..." 
                                            value={specificUserId} 
                                            onChange={(e) => setSpecificUserId(e.target.value)} 
                                            required
                                        />
                                    </div>
                                )}

                                <div className="input-group">
                                    <label>Headline</label>
                                    <input 
                                        type="text" 
                                        value={title} 
                                        onChange={(e) => setTitle(e.target.value)} 
                                        placeholder="Notification title"
                                        maxLength="50"
                                        required 
                                    />
                                    <span className="char-count">{title.length}/50</span>
                                </div>

                                <div className="input-group">
                                    <label>Message Body</label>
                                    <textarea 
                                        value={message} 
                                        onChange={(e) => setMessage(e.target.value)} 
                                        placeholder="Type your message here..."
                                        rows="4"
                                        maxLength="160"
                                        required
                                    />
                                    <span className="char-count">{message.length}/160</span>
                                </div>

                                <button className="transmit-btn" type="submit" disabled={loading}>
                                    {loading ? (
                                        <span className="loading-spinner"></span>
                                    ) : (
                                        <><span>Transmit Notification</span> <i className="btn-icon">⚡</i></>
                                    )}
                                </button>

                                {status && (
                                    <div className={`notification-status ${status.type}`}>
                                        <i className="status-icon">{status.type === 'success' ? '✓' : '⚠'}</i>
                                        <span className="status-text">{status.text}</span>
                                    </div>
                                )}
                            </form>
                        </section>
                    </div>

                    {/* Right: Live Preview */}
                    <div className="preview-column">
                        <section className="preview-card">
                            <h3>Live Device Preview</h3>
                            <div className="device-container">
                                <div className="device-mockup">
                                    <div className="device-screen">
                                        <div className="status-bar">
                                            <span>9:41</span>
                                            <div className="status-icons">📶 🔋</div>
                                        </div>
                                        
                                        <div className="notification-bubble">
                                            <div className="bubble-header">
                                                <div className="app-info">
                                                    <div className="app-icon-small">S</div>
                                                    <span className="app-name">SKILLAI</span>
                                                </div>
                                                <span className="time-now">now</span>
                                            </div>
                                            <div className="bubble-content">
                                                <h4 className="push-title">{title || 'Headline'}</h4>
                                                <p className="push-body">{message || 'Your message will appear here...'}</p>
                                            </div>
                                        </div>

                                        <div className="wallpaper-content">
                                            <div className="clock-widget">
                                                <h2>09:41</h2>
                                                <p>Tuesday, March 17</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="device-home-bar"></div>
                                </div>
                            </div>
                            <p className="preview-hint">Visual representation on iOS/Android devices</p>
                        </section>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PushNotificationManager;
